import { Component, ReactNode, ErrorInfo } from "react";
import { addReactError as datadogReactAddError } from "@datadog/browser-rum-react";

import { USE_DATADOG } from "common/utils/datadog";

/**
 * Props for the fallback render function
 */
export type FallbackProps = {
  error: Error;
  resetError: () => void;
};

/**
 * Context/tags to attach to the error report.
 */
export type ErrorContext = Record<string, string | number | boolean>;

export type ErrorBoundaryProps = {
  children: ReactNode;
  /**
   * Fallback UI to render when an error occurs.
   * Can be a ReactNode or a function that receives { error, resetError }.
   */
  fallback: ReactNode | ((props: FallbackProps) => ReactNode);
  /**
   * Callback when an error is caught. Use for custom cleanup logic (e.g., form cache reset).
   */
  onError?: () => void;
  /**
   * Additional context/tags to attach to the error report.
   */
  context?: ErrorContext;
  /**
   * Whether to report errors to error tracking services.
   * Defaults to true. Set to false for expected/noisy errors that don't
   * need alerting (e.g., PDF rendering failures).
   */
  shouldReportErrors?: boolean;
};

type ErrorBoundaryState = {
  error: Error | null;
};

/**
 * Error boundary that reports to Datadog RUM.
 *
 * This is used instead of Datadog's ErrorBoundary directly because we need
 * consistent support for:
 * - onError callback (for cleanup logic like form cache reset)
 * - resetError functionality
 * - context/tags on error reports
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { onError, context, shouldReportErrors = true } = this.props;
    const { componentStack } = errorInfo;

    // Always call custom onError handler if provided (e.g., for form cache reset)
    onError?.();

    if (!shouldReportErrors) {
      console.warn("[ErrorBoundary - Suppressed]", error, componentStack);
      return;
    }

    // Log error if no tracking service is enabled
    if (!USE_DATADOG) {
      console.error("[ErrorBoundary] No error tracking service enabled", error, componentStack);
      return;
    }

    // Attach context to the error for Datadog
    // eslint-disable-next-line no-param-reassign
    (error as Error & { dd_context?: ErrorContext }).dd_context = context;
    datadogReactAddError(error, errorInfo);
  }

  resetError = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (error) {
      if (typeof fallback === "function") {
        return fallback({ error, resetError: this.resetError });
      }
      return fallback;
    }

    return children;
  }
}

export default ErrorBoundary;
